'use client'

import { useEffect, useRef, useState } from 'react'
import { Modal } from '@repo/ui/Modal'
import { FormSearchAndSelect } from '@repo/form-components/FormSearchAndSelect'
import { useFormsStore } from '@repo/store/useFormsStore'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'

type LinkFormsModalProps = {
  open: boolean
  onClose: () => void
  workTypeId: string
  workTypeLabel: string
  existingFormIds: string[]
  onSaved: () => void | Promise<void>
}

export const LinkFormsModal = ({
  open,
  onClose,
  workTypeId,
  workTypeLabel,
  existingFormIds,
  onSaved
}: LinkFormsModalProps) => {
  const { updateAllFormWorkTypes } = useFormsStore()
  const [selectedFormIds, setSelectedFormIds] = useState<string[]>(existingFormIds)
  const [isSaving, setIsSaving] = useState(false)
  const wasOpenRef = useRef(open)

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setSelectedFormIds(existingFormIds)
    }
    wasOpenRef.current = open
  }, [open, existingFormIds])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await updateAllFormWorkTypes({ formIds: selectedFormIds, workTypeId })
      await onSaved()
      onClose()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      handleClose={onClose}
      title={`Link forms to Worktype: ${workTypeLabel}`}
      sx={{ maxHeight: '40rem', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <MUIStack data-testid="LinkFormsModal" sx={{ flex: 1, minHeight: 0 }}>
        <FormSearchAndSelect
          variant="checkbox"
          selectedFormIds={selectedFormIds}
          onSelectionChange={(next: string[]) => setSelectedFormIds(next)}
          workTypeId={workTypeId}
          sx={{ flex: 1, minHeight: 0 }}
          cardsContainerSx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 1 }}
        />
        <MUIStack direction="row" justifyContent="flex-end" gap={2} sx={{ flexShrink: 0, pt: 4 }}>
          <MUIButton variant="text" onClick={onClose} disabled={isSaving}>
            Cancel
          </MUIButton>
          <MUIButton variant="contained" onClick={handleSave} disabled={isSaving}>
            Save all
          </MUIButton>
        </MUIStack>
      </MUIStack>
    </Modal>
  )
}
